import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { ManagerOption } from "#shared/reviewee-filters/filterOptionsContract.ts";
import { ListBoxDescription, ListBoxItem, ListBoxLabel } from "@/components/ui/list-box.tsx";
import { useFilterOptionsClient } from "../../api/filterOptionsQuery.ts";
import { PagedLookupPanel, type PagedLookupPanelLabels } from "./PagedLookupPanel.tsx";
import { MY_SCOPE_ID, resolveScopeSelection, toScopeSelectedKeys } from "./resolveScopeSelection.ts";

// intent-ui-deviation: check-indicator moved to the row end — ListBoxItem renders it inline in column 1, where it
const CHECK_AT_ROW_END = "*:data-[slot=check-indicator]:inset-e-2 *:data-[slot=check-indicator]:absolute";

const MY_SCOPE_IDS = [MY_SCOPE_ID];

const PLACEHOLDER_PEOPLE: ManagerOption[] = [
    { id: "skeleton-1", displayName: "Xxxxxxx Xxxxxxxx", jobTitle: "Xxxxxxx xxxxxxxx" },
    { id: "skeleton-2", displayName: "Xxxxxx Xxxxxxxxx", jobTitle: "Xxxxxxxx xxxx" },
    { id: "skeleton-3", displayName: "Xxxxxxxx Xxxxx", jobTitle: "Xxxxx xxxxxxx xxxx" },
    { id: "skeleton-4", displayName: "Xxxx Xxxxxxx", jobTitle: "Xxxxxxxxx xxxxxxx" }
];

export interface PersonScopeFilterPanelProps {
    labels: PagedLookupPanelLabels;
    myScopeLabel: string;
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
    onBack: () => void;
    maxSelections?: number;
}

function renderPerson(person: ManagerOption) {
    return (
        <ListBoxItem id={person.id} textValue={`${person.displayName} ${person.jobTitle}`.trim()} className={`${CHECK_AT_ROW_END} *:data-[slot=check-indicator]:top-3`}>
            <PersonAvatar person={person} />
            <ListBoxLabel className="truncate pe-6">{person.displayName}</ListBoxLabel>
            {person.jobTitle && <ListBoxDescription className="truncate pe-6">{person.jobTitle}</ListBoxDescription>}
        </ListBoxItem>
    );
}

export function PersonScopeFilterPanel({ labels, myScopeLabel, selectedIds, onSelectionChange, onBack, maxSelections }: PersonScopeFilterPanelProps) {
    const client = useFilterOptionsClient();
    const callerId = useSession()?.memberId ?? "";
    const hasMyScope = callerId !== "";

    const fetchPage = useCallback(({ q, continuationToken }: { q: string; continuationToken: string | undefined }) => client.searchReportingManagers.call({ q, continuationToken }), [client]);

    const handleSelectionChange = useCallback((ids: string[]) => onSelectionChange(resolveScopeSelection(ids, callerId)), [onSelectionChange, callerId]);

    const scopeSelectedIds = useMemo(() => (hasMyScope ? toScopeSelectedKeys(selectedIds, callerId) : selectedIds), [hasMyScope, selectedIds, callerId]);

    const hiddenIds = useMemo(() => (hasMyScope ? [callerId] : undefined), [hasMyScope, callerId]);

    const [idsSelectedOnOpen] = useState(() => selectedIds.filter(id => id !== callerId));

    const selectedPeopleOptions = client.searchReportingManagers.queryOptions({ input: { restrictToMemberIds: idsSelectedOnOpen, pageSize: Math.max(idsSelectedOnOpen.length, 1) } });
    const { data: selectedPeoplePage } = useQuery(
        createQueryOptions(selectedPeopleOptions, {
            enabled: idsSelectedOnOpen.length > 0,
            throwOnError: false
        })
    );

    const pinnedRows = hasMyScope ? (
        <ListBoxItem id={MY_SCOPE_ID} textValue={myScopeLabel} className={`${CHECK_AT_ROW_END} border-border mb-1 border-b pb-2 *:data-[slot=check-indicator]:top-2`}>
            <ListBoxLabel className="pe-6 font-medium">{myScopeLabel}</ListBoxLabel>
        </ListBoxItem>
    ) : undefined;

    return (
        <PagedLookupPanel
            labels={labels}
            queryKey={client.searchReportingManagers.key()}
            fetchPage={fetchPage}
            placeholder={PLACEHOLDER_PEOPLE}
            selectedIds={scopeSelectedIds}
            onSelectionChange={handleSelectionChange}
            onBack={onBack}
            renderItem={renderPerson}
            pinnedRows={pinnedRows}
            selectedItems={selectedPeoplePage?.items}
            hiddenIds={hiddenIds}
            pinnedIds={hasMyScope ? MY_SCOPE_IDS : undefined}
            listBoxClassName="gap-y-0"
            maxSelections={maxSelections}
        />
    );
}
